import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DocumentStatusBadge } from "./DocumentStatusBadge";
import { Download, FileText, MapPin, Tag, Loader2, AlertCircle } from "lucide-react";
import { getDocumentDownloadUrl } from "@/lib/knowledge-base-service";
import type { KBDocument } from "@/lib/knowledge-base-service";
import { formatDistanceToNow } from "date-fns";

interface DocumentPreviewModalProps {
  doc: KBDocument | null;
  onClose: () => void;
  onDownload: (doc: KBDocument) => Promise<void>;
}

function formatSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentPreviewModal({ doc, onClose, onDownload }: DocumentPreviewModalProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState(false);

  const isPdf = doc?.file_type === "application/pdf" || doc?.name?.toLowerCase().endsWith(".pdf");
  const hasText = !!doc?.extracted_text?.trim();

  useEffect(() => {
    if (!doc?.file_path) return;
    setSignedUrl(null);
    setUrlError(false);
    setUrlLoading(true);

    getDocumentDownloadUrl(doc.file_path)
      .then(url => setSignedUrl(url))
      .catch(() => setUrlError(true))
      .finally(() => setUrlLoading(false));
  }, [doc?.file_path]);

  if (!doc) return null;

  const defaultTab = isPdf && doc.file_path ? "preview" : "text";

  return (
    <Dialog open={!!doc} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="flex-none px-5 pt-5 pb-4 border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 mt-0.5">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base font-semibold leading-snug truncate pr-2">
                  {doc.name}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}
                  {doc.file_size ? ` · ${formatSize(doc.file_size)}` : ""}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={() => onDownload(doc)}
              disabled={!doc.file_path}
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </Button>
          </div>

          {/* Metadata badges */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <DocumentStatusBadge status={doc.status} />
            {doc.jurisdiction && (
              <Badge variant="secondary" className="gap-1 text-xs">
                <MapPin className="h-2.5 w-2.5" />
                {doc.jurisdiction}
              </Badge>
            )}
            {doc.regulation_type && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Tag className="h-2.5 w-2.5" />
                {doc.regulation_type}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {isPdf && doc.file_path ? (
            <Tabs defaultValue={defaultTab} className="flex flex-col h-full">
              <TabsList className="flex-none mx-5 mt-3 w-fit">
                <TabsTrigger value="preview">PDF Preview</TabsTrigger>
                {hasText && <TabsTrigger value="text">Extracted Text</TabsTrigger>}
              </TabsList>

              <TabsContent value="preview" className="flex-1 min-h-0 mt-3 mx-5 mb-5">
                {urlLoading && (
                  <div className="flex h-full items-center justify-center rounded-xl border border-border bg-muted/30">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                {urlError && (
                  <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-border bg-muted/30 text-center">
                    <AlertCircle className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Could not load preview</p>
                    <Button variant="outline" size="sm" onClick={() => onDownload(doc)}>
                      Download instead
                    </Button>
                  </div>
                )}
                {signedUrl && (
                  <iframe
                    src={signedUrl}
                    title={doc.name}
                    className="w-full h-full rounded-xl border border-border"
                  />
                )}
              </TabsContent>

              {hasText && (
                <TabsContent value="text" className="flex-1 min-h-0 mt-3 mx-5 mb-5 overflow-hidden">
                  <ExtractedTextView text={doc.extracted_text!} />
                </TabsContent>
              )}
            </Tabs>
          ) : hasText ? (
            <div className="h-full p-5 overflow-hidden">
              <ExtractedTextView text={doc.extracted_text!} />
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center p-8">
              <FileText className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">No preview available</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                {doc.status === 'processing'
                  ? "This document is still being indexed. Check back in a moment."
                  : doc.status === 'failed'
                  ? "Text extraction failed for this file. You can still download the original."
                  : "No text content could be extracted from this file."}
              </p>
              {doc.file_path && (
                <Button variant="outline" size="sm" className="mt-2" onClick={() => onDownload(doc)}>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download original
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExtractedTextView({ text }: { text: string }) {
  return (
    <div className="h-full overflow-y-auto rounded-xl border border-border bg-muted/20 p-4">
      <pre className="text-xs text-foreground/80 whitespace-pre-wrap font-mono leading-relaxed">
        {text}
      </pre>
    </div>
  );
}
